import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/**
 * profiles テーブル (共有 Supabase) の行に対応する型。
 * 列は 0001/0006 マイグレーションと register/profile-edit フォームの読み書きから確定。
 * 任意入力・トリガー管理列は optional とし、確証の弱いものは VERIFY コメントを付す。
 */
export interface Profile {
  id: string;
  email: string;
  name: string;
  /** 任意入力 (register Step2 / profile-edit のセレクト)。未選択なら '' か null。 */
  gender?: string | null;
  /** 任意入力 (居住国)。未入力なら '' か null。 */
  country?: string | null;
  timezone: string;
  native_languages: string[];
  learning_languages: string[];
  is_matched: boolean;
  is_admin: boolean;
  subscription_status: 'none' | 'active' | 'paused' | 'cancelled' | 'past_due';
  /** 所属団体 (ToB)。個人登録なら null。 */
  organization_id: string | null;
  /** 団体管理者(org_admin)かどうか。word-pool 側の権限正本でもある。 */
  is_instructor: boolean;
  /** 表示補助ロール。権限の正本は is_admin / is_instructor / organization_id。 */
  role: 'member' | 'org_admin';
  /** アカウント有効フラグ (0001 protect トリガーで本人更新から保護)。admin 管理列。 */
  is_active?: boolean;
  /** レベルチェック結果のレベル。未受験なら null。admin 一覧で表示。VERIFY: 値域は test_results 由来。 */
  level?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  /** プロフィールが必須項目まで埋まっているか（DBトリガーで作られた直後は false）。 */
  profileComplete: boolean;
  signOut: () => Promise<void>;
  /** Step2 完了後など、最新の profiles を再取得して context を更新する。 */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  profileComplete: false,
  signOut: async () => {},
  refreshProfile: async () => {},
});

/**
 * profiles 行は DBトリガー(handle_new_user)で signup 直後に自動生成されるが、
 * その時点では name / timezone / 言語などが未入力。これらが揃って初めて
 * 「プロフィール完了」とみなし、各画面の利用を許可する。
 */
function isProfileComplete(p: Profile | null): boolean {
  if (!p) return false;
  return Boolean(
    p.name &&
    p.timezone &&
    p.native_languages?.length &&
    p.learning_languages?.length
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    // profiles 行が未生成/未完了でも .single() は error を返すだけなので握りつぶし null を入れる。
    // 各画面は profile=null / profileComplete=false でも壊れないようガードしている。
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    setProfile(data ?? null);
    setLoading(false);
  };

  const refreshProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setLoading(true);
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        profileComplete: isProfileComplete(profile),
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
